import { ThemedContainer, ThemeVariant } from "@amx/ux-twoui-theming";
import React from "react";
import { ActionButton, ActionButtonProps } from "../../Buttons/ActionButton/ActionButton";
import { Button, ButtonProps } from "../../Buttons/Button/Button";
import { LinkButtonProps } from "../../Buttons/shared/props";
import { Hyperlink, HyperlinkProps, RoutelinkProps } from "../../Links/Hyperlink/Hyperlink";
import { NoHrefLink, NoHrefLinkProps } from "../../Links/NoHrefLink/NoHrefLink";
import { StopPropagation } from "../../StopPropagation/StopPropagation";
import { Toggle, ToggleProps } from "../../Toggle/Toggle";
import { ToggleShimmer, ToggleShimmerProps } from "../../Toggle/ToggleShimmer/ToggleShimmer";

export interface FeatureCallToActionProps {
    /**
     * Feature action props. Choices are Button, Hyperlink, Toggle or ToggleShimmer.
     */
    readonly actionProps: FeatureActionProp;
}

export type FeatureActionProp = FeatureButtonProps | FeatureActionButtonProps | FeatureHyperlinkProps | FeatureNoHrefLinkProps | FeatureToggleProps | FeatureToggleShimmerProps;

export interface IrisFeatureCallToActionProps {
    /**
     * Feature action props. Choices are Button and NoHrefLink.
     */
    readonly actionProps: IrisFeatureActionProp;
}

// IrisFeature only supports button props with onClick, so we exclude LinkButton which uses an href instead of an OnClick
export type IrisFeatureActionButtonProps = Exclude<FeatureActionButtonProps, LinkButtonProps>;

export type IrisFeatureActionProp = IrisFeatureActionButtonProps | FeatureButtonProps | FeatureNoHrefLinkProps;

interface FeatureButtonProps extends ButtonProps {
    readonly kind: "button";
}

type FeatureActionButtonProps = ActionButtonProps & {
    readonly kind: "actionButton";
    readonly buttonTheme?: ThemeVariant;
};

type FeatureHyperlinkProps = (HyperlinkProps | RoutelinkProps) & {
    readonly kind: "hyperlink";
};

interface FeatureNoHrefLinkProps extends NoHrefLinkProps {
    readonly kind: "noHrefLink";
}

interface FeatureToggleProps extends ToggleProps {
    readonly kind: "toggle";
}

interface FeatureToggleShimmerProps extends ToggleShimmerProps {
    readonly kind: "toggleShimmer";
}

export const FeatureCallToAction: React.FC<FeatureCallToActionProps> = ({
    actionProps,
    ...otherProps
}) => {
    let actionElement: JSX.Element;
    switch (actionProps.kind) {
        case "button": {
            const { kind, ...buttonProps } = actionProps;
            actionElement = <Button {...buttonProps} />;
            break;
        }
        case "actionButton": {
            const { kind, ...actionButtonProps } = actionProps;
            actionElement = <ActionButton {...actionButtonProps} />;
            break;
        }
        case "hyperlink": {
            const { kind, ...hyperlinkProps } = actionProps;
            actionElement = <Hyperlink {...hyperlinkProps} />;
            break;
        }
        case "noHrefLink": {
            const { kind, ...noHrefLinkProps } = actionProps;
            actionElement = <NoHrefLink {...noHrefLinkProps} />;
            break;
        }
        case "toggle": {
            const { kind, ...toggleProps } = actionProps;
            actionElement = <Toggle {...toggleProps} />;
            break;
        }
        case "toggleShimmer": {
            const { kind, ...toggleShimmerProps } = actionProps;
            actionElement = <ToggleShimmer {...toggleShimmerProps} />;
            break;
        }
    }
    if (actionProps.kind === "actionButton" && actionProps.buttonTheme) {
        return (
            <StopPropagation {...otherProps}>
                <ThemedContainer variant={actionProps.buttonTheme} noRootElement={true}>
                    {actionElement}
                </ThemedContainer>
            </StopPropagation>
        );
    }
    return (
        <StopPropagation {...otherProps}>
            {actionElement}
        </StopPropagation>
    );
};
